import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "next-themes";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { BillingProvider } from "@/hooks/useBilling";
import ProtectedRoute from "@/components/ProtectedRoute";
import LanguageSync from "@/components/LanguageSync";
import CookieBanner from "@/components/CookieBanner";
import SEOHead from "@/components/SEOHead";
import Index from "./pages/Index";
import Onboarding from "./pages/Onboarding";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import DashboardLayout from "./components/dashboard/DashboardLayout";
import CalendarPage from "./pages/dashboard/CalendarPage";
import ReservationsPage from "./pages/dashboard/ReservationsPage";
import ReservationDetailPage from "./pages/dashboard/ReservationDetailPage";
import CreateReservationPage from "./pages/dashboard/CreateReservationPage";
import ServicesPage from "./pages/dashboard/ServicesPage";
import ServiceForm from "./pages/dashboard/ServiceForm";
import CustomersPage from "./pages/dashboard/CustomersPage";
import CustomerDetailPage from "./pages/dashboard/CustomerDetailPage";
import BlockedTimesPage from "./pages/dashboard/BlockedTimesPage";
import BlockedTimeForm from "./pages/dashboard/BlockedTimeForm";
import UpsellsPage from "./pages/dashboard/UpsellsPage";
import UpsellForm from "./pages/dashboard/UpsellForm";
import SettingsPage from "./pages/dashboard/SettingsPage";
import OrganisationSettings from "./pages/dashboard/settings/OrganisationSettings";
import OpeningHoursSettings from "./pages/dashboard/settings/OpeningHoursSettings";
import BookingSettings from "./pages/dashboard/settings/BookingSettings";
import CheckoutSettings from "./pages/dashboard/settings/CheckoutSettings";
import EmbedSettings from "./pages/dashboard/settings/EmbedSettings";
import ShopSettings from "./pages/dashboard/settings/ShopSettings";
import BillingSettings from "./pages/dashboard/settings/BillingSettings";
import ExtensionsSettings from "./pages/dashboard/settings/ExtensionsSettings";
import EmployeesExtensionSettings from "./pages/dashboard/settings/EmployeesExtensionSettings";
import TeamSettings from "./pages/dashboard/settings/TeamSettings";
import SelfServiceExtensionSettings from "./pages/dashboard/settings/SelfServiceExtensionSettings";
import PayAtOrderExtensionSettings from "./pages/dashboard/settings/PayAtOrderExtensionSettings";
import StatisticsPage from "./pages/dashboard/StatisticsPage";
import EmployeesPage from "./pages/dashboard/EmployeesPage";
import EmployeeForm from "./pages/dashboard/EmployeeForm";
import ShopBooking from "./pages/shop/ShopBooking";
import ManageBooking from "./pages/shop/ManageBooking";
import PaymentReturn from "./pages/shop/PaymentReturn";
import ProfilePage from "./pages/dashboard/ProfilePage";
import ExtensionLandingPage from "./pages/extensions/ExtensionLandingPage";
import NotFound from "./pages/NotFound";
import AcceptInvite from "./pages/AcceptInvite";
import AdminGuard from "./components/admin/AdminGuard";
import AdminLayout from "./components/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";

const queryClient = new QueryClient();

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
  <LanguageProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <AuthProvider>
          <BillingProvider>
          <LanguageSync />
          <SEOHead />
          <Toaster />
          <Sonner />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsConditions />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/invite" element={<AcceptInvite />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route index element={<CalendarPage />} />
              <Route path="reservations" element={<ReservationsPage />} />
              <Route path="reservations/new" element={<CreateReservationPage />} />
              <Route path="reservations/:id" element={<ReservationDetailPage />} />
              <Route path="services" element={<ServicesPage />} />
              <Route path="services/new" element={<ServiceForm />} />
              <Route path="services/:id/edit" element={<ServiceForm />} />
              <Route path="upsells" element={<UpsellsPage />} />
              <Route path="upsells/new" element={<UpsellForm />} />
              <Route path="upsells/:id/edit" element={<UpsellForm />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customers/:id" element={<CustomerDetailPage />} />
              <Route path="blocked" element={<BlockedTimesPage />} />
              <Route path="blocked/new" element={<BlockedTimeForm />} />
              <Route path="blocked/:id/edit" element={<BlockedTimeForm />} />
              <Route path="employees" element={<EmployeesPage />} />
              <Route path="employees/new" element={<EmployeeForm />} />
              <Route path="employees/:id/edit" element={<EmployeeForm />} />
              <Route path="statistics" element={<StatisticsPage />} />
              
              <Route path="settings" element={<SettingsPage />}>
                <Route path="organisation" element={<OrganisationSettings />} />
                <Route path="opening-hours" element={<OpeningHoursSettings />} />
                <Route path="booking" element={<BookingSettings />} />
                <Route path="checkout" element={<CheckoutSettings />} />
                <Route path="shop" element={<ShopSettings />} />
                <Route path="embed" element={<EmbedSettings />} />
                <Route path="billing" element={<BillingSettings />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="extensions" element={<ExtensionsSettings />} />
                <Route path="team" element={<TeamSettings />} />
                <Route path="extensions/self-service" element={<SelfServiceExtensionSettings />} />
                <Route path="extensions/employees" element={<EmployeesExtensionSettings />} />
                <Route path="extensions/pay-at-order" element={<PayAtOrderExtensionSettings />} />
              </Route>
            </Route>
            <Route path="/shop/:shopId" element={<ShopBooking />} />
            <Route path="/shop/:shopId/manage" element={<ManageBooking />} />
            <Route path="/shop/:shopId/payment" element={<PaymentReturn />} />
            <Route path="/extensions/:extensionId" element={<ExtensionLandingPage />} />
            <Route path="/admin" element={
              <AdminGuard>
                <AdminLayout />
              </AdminGuard>
            }>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          <CookieBanner />
          </BillingProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </LanguageProvider>
  </ThemeProvider>
);

export default App;
